<template>
  <generic-modal
    :title="modal_title"
    ref="generic_modal"
    modal_class="is-full-height"
  >
    <!-- Información del ticket -->
    <div class="box">
      <div class="columns">
        <div class="column is-6">
          <div class="field">
            <label class="label">{{ $t("title") }}</label>
            <p class="content">{{ meta.title }}</p>
          </div>

          <div class="field">
            <label class="label">{{ $t("description") }}</label>
            <div class="content">
              <p v-html="meta.description_formatted"></p>
            </div>
          </div>

          <div class="field" v-if="meta.customer">
            <label class="label">{{ $t("customer") }}</label>
            <p class="content">{{ meta.customer.name }}</p>
          </div>
        </div>

        <div class="column is-6">
          <div class="field" v-if="meta.status">
            <label class="label">{{ $t("status") }}</label>
            <p class="content">
              <span class="tag" :class="status_class">{{
                meta.status.name
              }}</span>
            </p>
          </div>

          <div class="field" v-if="meta.priority">
            <label class="label">{{ $t("priority") }}</label>
            <p class="content">
              <span class="tag" :class="priority_class">{{
                meta.priority.name
              }}</span>
            </p>
          </div>

          <div class="field" v-if="meta.category">
            <label class="label">{{ $t("category") }}</label>
            <p class="content">{{ meta.category.name }}</p>
          </div>

          <div class="field" v-if="meta.assigned_agent">
            <label class="label">{{ $t("assigned_agent") }}</label>
            <p class="content">
              {{ meta.assigned_agent.name || $t("unassigned") }}
            </p>
          </div>

          <div class="field">
            <label class="label">{{ $t("created_date") }}</label>
            <p class="content">{{ format_date(meta.create_date) }}</p>
          </div>

          <div class="field" v-if="meta.resolved_date">
            <label class="label">{{ $t("resolved_date") }}</label>
            <p class="content">{{ format_date(meta.resolved_date) }}</p>
          </div>
        </div>
      </div>

      <div v-if="meta.resolution_notes" class="field">
        <label class="label">{{ $t("resolution_notes") }}</label>
        <div class="content">
          <p v-html="meta.resolution_notes_formatted"></p>
        </div>
      </div>
    </div>

    <!-- Sección de comentarios -->
    <div class="box">
      <h4 class="title is-4">{{ $t("comments") }}</h4>

      <div
        class="comments-container"
        style="max-height: 400px; overflow-y: auto"
      >
        <div
          v-if="comments.length === 0"
          class="has-text-centered has-text-grey"
        >
          <p>{{ $t("no_comments_yet") }}</p>
        </div>

        <article
          v-for="comment in comments"
          :key="comment.comment_id"
          class="media mb-4"
        >
          <figure class="media-left">
            <p class="image is-32x32">
              <i class="fa fa-user-circle fa-2x has-text-grey-light"></i>
            </p>
          </figure>
          <div class="media-content">
            <div class="content">
              <p>
                <strong>{{ comment.user_name }}</strong>
                <small class="has-text-grey">{{
                  format_date(comment.created_date)
                }}</small>
                <br />
                <span v-html="comment.comment_formatted"></span>
              </p>
            </div>
          </div>
        </article>
      </div>

      <!-- Agregar nuevo comentario -->
      <div class="field mt-4">
        <label class="label">{{ $t("add_comment") }}</label>
        <div class="control">
          <textarea
            class="textarea"
            v-model="new_comment"
            :placeholder="$t('write_comment')"
            rows="3"
          ></textarea>
        </div>
      </div>

      <div class="field">
        <div class="control">
          <button
            class="button is-primary"
            @click="add_comment"
            :disabled="!new_comment.trim()"
          >
            <span class="icon"><i class="fa fa-comment"></i></span>
            <span>{{ $t("add_comment") }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Manejo de errores -->
    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>

    <!-- Footer con botones -->
    <template v-slot:footer>
      <button class="button is-warning is-rounded" @click="edit_ticket">
        <span class="icon"><i class="fa fa-edit"></i></span>
        <span>{{ $t("edit_ticket") }}</span>
      </button>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon"><i class="fa fa-close"></i></span>
        <span>{{ $t("close") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import { put, get, generate_error_message_text } from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";

export default {
  name: "TicketDetailModal",
  components: { GenericModal },
  data() {
    return {
      meta: {},
      comments: [],
      new_comment: "",
      error: "",
    };
  },
  computed: {
    modal_title() {
      return `${this.$t("ticket")} #${this.meta.ticket_number || ""}`;
    },
    status_class() {
      if (!this.meta.status_name) return "";
      const status = this.meta.status_name.toLowerCase();
      if (status.includes("abierto") || status.includes("open"))
        return "is-success";
      if (status.includes("progreso") || status.includes("progress"))
        return "is-warning";
      if (status.includes("cerrado") || status.includes("closed"))
        return "is-dark";
      if (status.includes("resuelto") || status.includes("resolved"))
        return "is-info";
      return "is-light";
    },
    priority_class() {
      if (!this.meta.priority_name) return "";
      const priority = this.meta.priority_name.toLowerCase();
      if (priority.includes("alta") || priority.includes("high"))
        return "is-danger";
      if (priority.includes("media") || priority.includes("medium"))
        return "is-warning";
      if (priority.includes("baja") || priority.includes("low"))
        return "is-success";
      return "is-light";
    },
  },
  methods: {
    close_modal() {
      this.$refs.generic_modal.close_modal();
      this.set_initial_state();
    },

    show_modal(uuid) {
      this.set_initial_state();

      if (!uuid) {
        this.error = this.$t("ticket_not_found");
        return;
      }

      // Cargar información del ticket
      get("/helpdesk/ticket", { uuid })
        .then((ticket) => {
          this.meta = {
            ...ticket,
            description_formatted: this.format_text(ticket.description),
            resolution_notes_formatted: this.format_text(
              ticket.resolution_notes
            ),
          };

          this.$refs.generic_modal.show_modal();
          this.load_comments();
        })
        .catch((error) => {
          this.error = generate_error_message_text(error);
        });
    },

    load_comments() {
      get("/helpdesk/comment/catalog", { ticket_id: this.meta.ticket_id })
        .then((comments) => {
          this.comments = comments.map((comment) => ({
            ...comment,
            comment_formatted: this.format_text(comment.comment),
          }));
        })
        .catch((error) => {
          console.error("Error loading comments:", error);
        });
    },

    add_comment() {
      if (!this.new_comment.trim()) return;

      const comment_data = {
        ticket_id: this.meta.ticket_id,
        comment_text: this.new_comment.trim(),
      };

      put("/helpdesk/comment", comment_data)
        .then(() => {
          this.new_comment = "";
          this.load_comments();
          this.$emit("comment_added");
        })
        .catch((error) => {
          this.error = generate_error_message_text(error);
        });
    },

    edit_ticket() {
      this.$emit("edit_ticket", this.meta.uuid);
      this.close_modal();
    },

    format_date(date) {
      if (!date) return "";
      return new Date(date).toLocaleString("es-GT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    format_text(text) {
      if (!text) return "";
      return text.replace(/\n/g, "<br>");
    },

    set_initial_state() {
      this.meta = {};
      this.comments = [];
      this.new_comment = "";
      this.error = "";
    },
  },
};
</script>

<style scoped>
.comments-container {
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 1rem;
  background-color: #fafafa;
}

.media {
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 1rem;
}

.media:last-child {
  border-bottom: none;
  margin-bottom: 0;
}
</style>
